import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { useGetGrantWithSessionsQuery } from '../../../app/redux-fetch/apiImpersonation'
import messages from './messages'

// E11 R5 (WI 612) — the grants-row detail panel: the sessions started under a grant, oldest-first. MRT only
// mounts this for an EXPANDED row, so the query fires lazily on expand. The grant's own summary comes back in
// the payload too but the row already shows it; here we render just the sessions.
interface GrantSessionsPanelProps {
  grantId: number
}

const fmt = (iso?: string | null) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '—')

// The session end reasons (ImpersonationSessionEndReason 1-6); null while active.
const endReasonMessageKey: Record<number, keyof typeof messages> = {
  1: 'endReasonUserEnded',
  2: 'endReasonGrantExpired',
  3: 'endReasonGrantRevoked',
  4: 'endReasonSignedOut',
  5: 'endReasonAdminTerminated',
  6: 'endReasonTokenRevoked',
}

const GrantSessionsPanel = ({ grantId }: GrantSessionsPanelProps) => {
  const { formatMessage } = useIntl()
  const { data, isFetching, isError } = useGetGrantWithSessionsQuery(grantId)

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size={16} />
        <Typography variant='body2'>{formatMessage(messages.sessionsLoading)}</Typography>
      </Box>
    )
  }

  if (isError) {
    return (
      <Typography variant='body2' color='error' sx={{ p: 2 }}>
        {formatMessage(messages.sessionsError)}
      </Typography>
    )
  }

  const sessions = data?.sessions ?? []
  if (sessions.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
        {formatMessage(messages.sessionsNone)}
      </Typography>
    )
  }

  return (
    <Box sx={{ p: 1 }}>
      <Table size='small' aria-label='grant-sessions'>
        <TableHead>
          <TableRow>
            <TableCell>{formatMessage(messages.sessionStarted)}</TableCell>
            <TableCell>{formatMessage(messages.sessionEnded)}</TableCell>
            <TableCell>{formatMessage(messages.sessionEndReasonColumn)}</TableCell>
            <TableCell>{formatMessage(messages.sessionEndedBy)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((s) => {
            const reasonKey = s.endReason != null ? endReasonMessageKey[s.endReason] : undefined
            return (
              <TableRow key={s.sessionId}>
                <TableCell>{fmt(s.startedAt)}</TableCell>
                <TableCell>
                  {s.endedAt ? (
                    fmt(s.endedAt)
                  ) : (
                    <Chip size='small' color='success' label={formatMessage(messages.sessionActive)} />
                  )}
                </TableCell>
                <TableCell>{reasonKey ? formatMessage(messages[reasonKey]) : '—'}</TableCell>
                <TableCell>{s.endedByDisplayName?.trim() || '—'}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default GrantSessionsPanel
